import {
  Box,
  Flex,
  IconButton,
  ListItem,
  Spinner,
  Text,
  Tooltip,
  UnorderedList,
  useToast,
} from '@chakra-ui/react';
import type {
  VoucherEligibilityItem,
  VoucherGetByCodeResponse,
} from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import {
  formatDateRange,
  getItemCardTrackingParams,
  trackEvent,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { getVoucherTrackingParams } from '@diamond/sol-buyer/utils';
import {
  getProductDetailOptions,
  useAuthStore,
  useGetProductDetail,
  useInvalidateVoucherList,
  useShipToStore,
} from '@diamond/sol-buyer-context';
import { CalendarMonthOutlined } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';

import { AddToCartPopup } from '../common/add-to-cart-popup/add-to-cart-popup';
import { AddToCartIcon, ChecklistIconVoucher } from './icons';
import {
  type VoucherRuleProductAction,
  mapVoucherRuleProducts,
} from './rule-product-utils';
import VoucherRuleProductActions from './VoucherRuleProductActions';

type VoucherDisplayItem = VoucherEligibilityItem | VoucherGetByCodeResponse;

type MessageRuleProduct = {
  message: string;
  products?: Array<{
    product_item_code: number;
    product_name: string;
    min_quantity: number | null;
  }>;
};

interface VoucherCardProps {
  voucher: VoucherDisplayItem;
  isDisabled?: boolean;
  isApplied?: boolean;
  isProcessing?: boolean;
  actionMode?: 'apply' | 'add_to_cart';
  messageRuleProducts?: MessageRuleProduct[];
  onApply?: (voucher: VoucherDisplayItem) => void;
  onShortcutAddToCart?: (voucher: VoucherDisplayItem) => void;
  onClick?: (voucher: VoucherDisplayItem) => void;
  onCartSyncStart?: () => void;
  onCartSyncComplete?: () => void;
}

type VoucherStatusFilter = 'all' | 'available' | 'disabled';

type SelectedProductInfo = {
  productId: string;
  itemCode: number;
  productName: string;
  minQuantity: number | null;
  missingQuantity?: number | null;
  minOrderQuantity?: number | null;
  orderQuantityMultiple?: number | null;
  defaultQuantity?: number | null;
  isOutOfStock?: boolean;
};

const getVoucherDates = (
  voucher: VoucherDisplayItem
): { startDate: string | Date; endDate: string | Date } => {
  if ('period_start' in voucher) {
    return {
      startDate: new Date(voucher.period_start * 1000).toISOString(),
      endDate: new Date(voucher.period_end * 1000).toISOString(),
    };
  }
  return {
    startDate: voucher.period_start_date,
    endDate: voucher.period_end_date,
  };
};

const VoucherCard = ({
  voucher,
  isDisabled,
  isApplied,
  isProcessing,
  actionMode = 'apply',
  messageRuleProducts,
  onApply,
  onShortcutAddToCart,
  onClick,
  onCartSyncStart,
  onCartSyncComplete,
}: VoucherCardProps) => {
  const token = useAuthStore((state) => state.access_token);
  const selectedShipToId = useShipToStore((state) => state.selectedShipToId);
  const { invalidate: invalidateVoucherList } = useInvalidateVoucherList();
  const queryClient = useQueryClient();
  const toast = useToast();
  const cardRef = useRef<HTMLDivElement>(null);
  const hasTrackedViewRef = useRef(false);
  const [isAddToCartOpen, setIsAddToCartOpen] = useState(false);
  const [selectedProductInfo, setSelectedProductInfo] =
    useState<SelectedProductInfo | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );

  const { data: selectedProductDetail } = useGetProductDetail(
    selectedProductId ?? undefined,
    selectedShipToId ?? undefined
  );

  const VISIBILITY_THRESHOLD = 0.65;
  const RESET_THRESHOLD = 0.1;

  useEffect(() => {
    const element = cardRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= VISIBILITY_THRESHOLD
          ) {
            if (!hasTrackedViewRef.current) {
              hasTrackedViewRef.current = true;
              trackEvent(TRACKING_EVENTS.VIEW_VOUCHER, {
                ...getVoucherTrackingParams(voucher),
                page_name: TRACKING_PAGES.VOUCHER,
                section_name: TRACKING_PAGES.VOUCHER_LIST,
              });
            }
          } else if (entry.intersectionRatio < RESET_THRESHOLD) {
            hasTrackedViewRef.current = false;
          }
        });
      },
      { threshold: [0, RESET_THRESHOLD, VISIBILITY_THRESHOLD] }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [voucher]);
  const ruleProductGroups = mapVoucherRuleProducts(voucher);
  const isBundlingVoucher =
    'reward' in voucher ? Boolean(voucher.reward?.is_bundling) : false;
  const hasSingleProductRule =
    'reward' in voucher
      ? voucher.reward?.reward_rule_products?.length === 1
      : false;
  const canShowRuleActions =
    !isBundlingVoucher && hasSingleProductRule && ruleProductGroups.length > 0;
  const shouldShowRuleAddButton = actionMode === 'apply' && canShowRuleActions;
  const hasWarningMessages =
    ruleProductGroups.length > 0 ||
    Boolean(messageRuleProducts && messageRuleProducts.length > 0);

  const isVoucherEligible =
    'is_eligible' in voucher ? voucher.is_eligible : true;
  const isActive = !isDisabled && isVoucherEligible;
  const titleColor = isActive ? '#000000' : '#8E8E93';
  const codeColor = isActive ? '#007AFF' : '#8E8E93';
  const isAddToCartMode = actionMode === 'add_to_cart';
  const primaryRuleProduct = ruleProductGroups[0]?.products[0];
  const canShortcutAdd =
    Boolean(token) &&
    Boolean(selectedShipToId) &&
    Boolean(primaryRuleProduct) &&
    !primaryRuleProduct?.isOutOfStock;

  const bannerUrl = voucher.banner_url || '/images/voucher.png';
  const { startDate, endDate } = getVoucherDates(voucher);

  const shouldShowWarning =
    actionMode === 'add_to_cart'
      ? hasWarningMessages
      : (!isVoucherEligible || isDisabled) && hasWarningMessages;

  const handleCheckboxClick = (e: React.MouseEvent) => {
    if (actionMode !== 'apply') {
      return;
    }

    e.stopPropagation();
    if (!isDisabled && !isProcessing && isVoucherEligible) {
      onApply?.(voucher);
    }
  };

  const handleShortcutAddToCart = () => {
    if (onShortcutAddToCart) {
      onShortcutAddToCart(voucher);
      return;
    }

    if (primaryRuleProduct) {
      void handleRuleProductAdd(primaryRuleProduct);
      return;
    }

    showToast(
      toast,
      'info',
      'Produk terkait voucher belum tersedia',
      undefined,
      'bottom',
      3000
    );
  };

  const handleCloseAddToCart = () => {
    setIsAddToCartOpen(false);
    setSelectedProductInfo(null);
    setSelectedProductId(null);
  };

  const handleAddToCartSuccess = () => {
    void invalidateVoucherList();
    showToast(
      toast,
      'success',
      'Berhasil menambahkan produk ke keranjang',
      undefined,
      'bottom',
      3000
    );
    handleCloseAddToCart();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (actionMode !== 'apply') {
      return;
    }

    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (!isDisabled && !isProcessing && isVoucherEligible) {
        onApply?.(voucher);
      }
    }
  };

  const handleRuleProductAdd = async (product: VoucherRuleProductAction) => {
    if (product.isOutOfStock || !token || !selectedShipToId) {
      return;
    }

    const quantity =
      product.missingQuantity > 0
        ? product.missingQuantity
        : product.minOrderQuantity ?? 1;

    const productDetail = await queryClient.fetchQuery(
      getProductDetailOptions(token, product.productId, selectedShipToId)
    );

    trackEvent(TRACKING_EVENTS.PRE_ADD_TO_CART, {
      ...getItemCardTrackingParams(
        productDetail,
        quantity,
        TRACKING_PAGES.VOUCHER_LIST,
        TRACKING_PAGES.VOUCHER_LIST
      ),
      source: 'voucher-card',
    });

    setSelectedProductId(product.productId);
    setSelectedProductInfo({
      productId: product.productId,
      itemCode: product.productItemCode,
      productName: product.productName,
      minQuantity: product.minOrderQuantity ?? null,
      missingQuantity:
        product.missingQuantity > 0
          ? product.missingQuantity
          : product.minOrderQuantity ?? null,
      minOrderQuantity: product.minOrderQuantity ?? null,
      orderQuantityMultiple: product.orderQuantityMultiple,
      defaultQuantity:
        product.normalizedMissingQuantity > 0
          ? product.normalizedMissingQuantity
          : product.minOrderQuantity ?? null,
      isOutOfStock: product.isOutOfStock,
    });
    setIsAddToCartOpen(true);
  };

  const renderCheckbox = () => {
    const baseStyles = {
      w: { base: '18px', lg: '24px' },
      h: { base: '18px', lg: '24px' },
      borderRadius: '4px',
      border: '2px',
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
      flexShrink: 0,
      role: 'checkbox',
      'aria-checked': isApplied,
      'aria-disabled': isDisabled || isProcessing || !isVoucherEligible,
      'aria-label': `Select voucher ${voucher.code}`,
      tabIndex: isDisabled || !isVoucherEligible ? -1 : 0,
      onKeyDown: handleKeyDown,
      onClick: handleCheckboxClick,
    };

    if (isDisabled || !isVoucherEligible) {
      return (
        <Flex
          {...baseStyles}
          borderColor="#8E8E93"
          bg={isApplied ? '#8E8E93' : 'transparent'}
          opacity={0.6}
          cursor="not-allowed"
        >
          {isApplied && (
            <ChecklistIconVoucher color="white" w="24px" h="24px" />
          )}
        </Flex>
      );
    }

    if (isProcessing) {
      return (
        <Flex
          {...baseStyles}
          borderColor={isApplied ? '#34C759' : '#8E8E93'}
          bg={isApplied ? '#34C759' : 'transparent'}
          cursor="wait"
        >
          <Spinner size="xs" color={isApplied ? 'white' : '#34C759'} />
        </Flex>
      );
    }

    if (isApplied) {
      return (
        <Flex
          {...baseStyles}
          border="none"
          cursor="pointer"
          _hover={{ opacity: 0.8 }}
        >
          <ChecklistIconVoucher color="white" w="24px" h="24px" />
        </Flex>
      );
    }

    return (
      <Flex
        {...baseStyles}
        borderColor="#0941A8"
        bg="transparent"
        cursor="pointer"
        _hover={{ borderColor: '#007AFF' }}
      />
    );
  };

  const renderShortcutAddButton = () => {
    return (
      <Tooltip
        label="Tambahkan produk terkait voucher"
        placement="top"
        hasArrow
        shouldWrapChildren
      >
        <Box as="span" pointerEvents="auto">
          <IconButton
            aria-label="Tambahkan produk voucher ke keranjang"
            icon={<AddToCartIcon color="#0000A3" boxSize="16px" />}
            size="sm"
            variant="ghost"
            minW="auto"
            h="32px"
            w="32px"
            p="0"
            bg="#F2F2F7"
            borderRadius="6px"
            _hover={{ bg: '#E9E9EF' }}
            onClick={(event) => {
              event.stopPropagation();
              handleShortcutAddToCart();
            }}
            isDisabled={Boolean(isProcessing) || !canShortcutAdd}
          />
        </Box>
      </Tooltip>
    );
  };

  const renderActionControl = () => {
    if (isDisabled) {
      return null;
    }

    return renderCheckbox();
  };

  return (
    <Flex
      ref={cardRef}
      w="100%"
      borderRadius="lg"
      borderWidth="1px"
      borderColor={isApplied ? 'transparent' : '#F2F2F7'}
      _hover={{ borderColor: '#007AFF' }}
      p={{ base: '8px', md: '12px' }}
      gap={{ base: '8px', md: '12px' }}
      direction="column"
      bgColor="white"
      onClick={onClick ? () => onClick(voucher) : undefined}
      cursor={onClick ? 'pointer' : 'default'}
      minW={0}
      overflow="hidden"
      boxSizing="border-box"
    >
      <Box opacity={isActive ? 1 : 0.6}>
        <Flex w="100%" gap={{ base: '8px', md: '12px' }} alignItems="center">
          <Box
            position="relative"
            w={{ base: '130px', md: '174px' }}
            sx={{ aspectRatio: '174/68' }}
            overflow="hidden"
            borderRadius={8}
            flexShrink={0}
          >
            <Image
              src={bannerUrl}
              alt={voucher.title}
              width={174}
              height={68}
              style={{
                borderRadius: 8,
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              }}
            />
          </Box>

          <Flex flex="1" gap={0} direction="column" minW={0} overflow="hidden">
            <Text
              fontSize={{ base: 'xs', lg: 'sm' }}
              textTransform="capitalize"
              fontWeight="medium"
              color={titleColor}
              noOfLines={{ base: 1, md: 0 }}
            >
              {voucher.title}
            </Text>

            <Text
              fontSize={{ base: 'xs', lg: 'sm' }}
              fontWeight="semibold"
              color={codeColor}
              noOfLines={{ base: 1, md: 0 }}
            >
              {voucher.code}
            </Text>

            <Flex
              alignItems={{ base: 'flex-start', lg: 'center' }}
              gap={{ base: '2px', lg: '6px' }}
            >
              <CalendarMonthOutlined
                sx={{ width: 16, height: 16, color: '#8E8E93', flexShrink: 0 }}
              />
              <Text fontSize="xs" color="#8E8E93">
                {formatDateRange(startDate, endDate)}
              </Text>
            </Flex>
          </Flex>

          {actionMode === 'apply' ? (
            <Flex
              alignItems="center"
              justifyContent="center"
              minW={{ base: '18px', lg: '24px' }}
              flexShrink={0}
            >
              {renderActionControl()}
            </Flex>
          ) : null}
        </Flex>
      </Box>

      {(shouldShowWarning || isAddToCartMode) && (
        <Box w="100%">
          <Flex
            align="flex-end"
            justify={shouldShowWarning ? 'space-between' : 'flex-end'}
            gap="12px"
          >
            {shouldShowWarning && (
              <Box flex="1" minW={0}>
                {ruleProductGroups.length > 0 ? (
                  <VoucherRuleProductActions
                    groups={ruleProductGroups}
                    isDisabled={!token || !selectedShipToId || !canShowRuleActions}
                    showAddButton={shouldShowRuleAddButton}
                    onAddProduct={handleRuleProductAdd}
                  />
                ) : (
                  <UnorderedList spacing="2px" pl="16px" m={0} color="#FF8D28">
                    {messageRuleProducts?.map((ruleProduct, idx) => {
                      return (
                        <ListItem key={idx}>
                          <Flex align="center" justify="space-between" gap="12px">
                            <Text fontSize="12px" color="#FF8D28">
                              {ruleProduct.message}
                            </Text>
                          </Flex>
                        </ListItem>
                      );
                    })}
                  </UnorderedList>
                )}
              </Box>
            )}
            {isAddToCartMode && renderShortcutAddButton()}
          </Flex>
        </Box>
      )}
      {selectedProductInfo && token && selectedShipToId && isAddToCartOpen && (
        <AddToCartPopup
          isOpen={isAddToCartOpen}
          onClose={handleCloseAddToCart}
          product={selectedProductDetail}
          productId={selectedProductInfo.productId}
          token={token}
          selectedShipToId={selectedShipToId}
          section="Voucher Warning"
          page="Voucher List"
          defaultQuantity={selectedProductInfo.defaultQuantity ?? undefined}
          autoSelectProductId={selectedProductInfo.productId}
          onSuccess={handleAddToCartSuccess}
          enableCartStoreSync={true}
          shouldAutoSelectOnAdd={true}
          onSyncStart={onCartSyncStart}
          onSyncComplete={onCartSyncComplete}
        />
      )}
    </Flex>
  );
};

export default VoucherCard;
export type { VoucherStatusFilter };
